// src/components/layout/Sidebar.jsx  
import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation, Link, useNavigate } from 'react-router-dom';
import {
  FiHome, FiSearch, FiBriefcase, FiUser, FiMessageSquare,
  FiBarChart2, FiSettings, FiChevronRight, FiX, FiDollarSign
} from 'react-icons/fi';
import { Sparkles, LogOut, ChevronUp, UserCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/images/logo.png';

const Sidebar = ({ onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, logout } = useAuth();
  const userRole = userProfile?.role;

  // Dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      setIsDropdownOpen(false);
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Navigation items based on user role
  const getNavigationItems = () => {
    const commonItems = [
      {
        name: 'Dashboard',
        href: '/app',
        icon: FiHome,
      }
    ];

    if (userRole === 'va') {
      return [
        ...commonItems,
        {
          name: 'Jobs',
          icon: FiBriefcase,
          subItems: [
            {
              name: 'Browse Jobs',
              href: '/app/jobs/browse',
              icon: FiSearch,
            },
            {
              name: 'My Jobs',
              href: '/app/jobs/my-jobs',
              icon: FiBriefcase,
            },
            {
              name: 'Applications',
              href: '/app/jobs/applications',
              icon: FiUser,
            }
          ]
        },
        {
          name: 'Profile',
          href: '/app/profile/va-setup',
          icon: FiUser,
        },
        {
          name: 'Earnings',
          href: '/earnings',
          icon: FiDollarSign,
        },
        {
          name: 'Messages',
          href: '/app/communication/messages',
          icon: FiMessageSquare,
        },
        {
          name: 'Settings',
          href: '/app/settings',
          icon: FiSettings,
        }
      ];
    } else {
      // Client navigation
      return [
        ...commonItems,
        {
          name: 'Jobs',
          icon: FiBriefcase,
          subItems: [
            {
              name: 'Post Job',
              href: '/app/jobs/post',
              icon: FiSearch,
            },
            {
              name: 'Manage Jobs',
              href: '/app/jobs/browse',
              icon: FiBriefcase,
            },
            {
              name: 'Browse VAs',
              href: '/app/jobs/browse-vas',
              icon: FiUser,
            }
          ]
        },
        {
          name: 'Communication',
          icon: FiMessageSquare,
          subItems: [
            {
              name: 'Messages',
              href: '/app/communication/messages',
              icon: FiMessageSquare,
            },
            {
              name: 'Email Templates',
              href: '/app/email/templates',
              icon: FiMessageSquare,
            },
            {
              name: 'Email History',
              href: '/app/email/history',
              icon: FiMessageSquare,
            }
          ]
        },
        {
          name: 'Analytics',
          href: '/app/analytics',
          icon: FiBarChart2,
        },
        {
          name: 'Settings',
          href: '/app/settings',
          icon: FiSettings,
        }
      ];
    }
  };

  const navigationItems = getNavigationItems();

  const renderNavigationItem = (item) => {
    if (item.subItems) {
      // Parent item with sub-items
      const isActive = item.subItems.some(subItem =>
        location.pathname === subItem.href ||
        (subItem.href !== '/' && location.pathname.startsWith(subItem.href))
      );

      return (
        <div key={item.name} className="space-y-1">
          <div className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg ${isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600'
            }`}>
            <item.icon className="mr-3 h-5 w-5" />
            <span className="flex-1">{item.name}</span>
            {item.badge && (
              <span className={`ml-2 px-2 py-1 text-xs font-bold text-white rounded-full ${item.badgeColor}`}>
                {item.badge}
              </span>
            )}
          </div>

          <div className="ml-4 space-y-1">
            {item.subItems.map(subItem => {
              const isSubActive = location.pathname === subItem.href ||
                (subItem.href !== '/' && location.pathname.startsWith(subItem.href));

              return (
                <NavLink
                  key={subItem.name}
                  to={subItem.href}
                  onClick={onClose}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isSubActive
                    ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                >
                  <subItem.icon className="mr-3 h-4 w-4" />
                  <span className="flex-1">{subItem.name}</span>
                  {isSubActive && (
                    <FiChevronRight className="ml-2 h-3 w-3" />
                  )}
                </NavLink>
              );
            })}
          </div>
        </div>
      );
    }

    // Regular navigation item
    const isActive = location.pathname === item.href ||
      (item.href !== '/' && location.pathname.startsWith(item.href));

    return (
      <NavLink
        key={item.name}
        to={item.href}
        onClick={onClose}
        className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
          ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
      >
        <item.icon className="mr-3 h-5 w-5" />
        <span className="flex-1">{item.name}</span>

        {item.badge && (
          <span className={`ml-2 px-2 py-1 text-xs font-bold text-white rounded-full ${item.badgeColor}`}>
            {item.badge}
          </span>
        )}

        {isActive && (
          <FiChevronRight className="ml-2 h-4 w-4" />
        )}
      </NavLink>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between h-16 px-2 border-b border-gray-200">
        <div className="flex items-center">
          <Link to="/" className="pt-3 flex items-center gap-3">
            <img src={logo} alt="ParrotOffice" className="h-8 w-auto" />
          </Link>
        </div>

        {/* Close button for mobile */}
        <button
          onClick={onClose}
          className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
        >
          <FiX size={20} />
        </button>
      </div>

      {/* Role Indicator */}
      {userRole && (
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${userRole === 'va' ? 'bg-green-500' : 'bg-blue-500'
              }`}></div>
            <span className="text-sm font-medium text-gray-700">
              {userRole === 'va' ? 'Virtual Assistant' : 'Client'}
            </span>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        {navigationItems.map(renderNavigationItem)}
      </nav>

      {/* User Profile Section */}
      <div className="border-t border-gray-200">
        {user && userProfile ? (
          <div className="relative" ref={dropdownRef}>
            {/* User Profile Button */}
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full p-4 flex items-center hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center flex-1 min-w-0">
                {/* Avatar */}
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
                  {userProfile.displayName ?
                    userProfile.displayName.charAt(0).toUpperCase() :
                    userProfile.email?.charAt(0).toUpperCase() || 'U'}
                </div>

                {/* User Info */}
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {userProfile.displayName || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {userProfile.email}
                  </p>
                </div>

                {/* Dropdown Icon */}
                <ChevronUp
                  className={`ml-2 flex-shrink-0 h-4 w-4 text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''
                    }`}
                />
              </div>
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-1 mx-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                <Link
                  to="/app/settings"
                  onClick={() => {
                    setIsDropdownOpen(false);
                    onClose?.();
                  }}
                  className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FiSettings className="mr-3 h-4 w-4 text-gray-500" />
                  <span>Settings & Profile</span>
                </Link>

                <div className="border-t border-gray-100" />

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="mr-3 h-4 w-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Sign In Button for non-authenticated users */
          <div className="p-4">
            <Link
              to="/login"
              onClick={onClose}
              className="w-full flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-sm hover:shadow-md"
            >
              <UserCircle className="mr-2 h-5 w-5" />
              <span className="font-medium">Sign In</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;